<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div class="message-content" data-cy="message-content-page">
    <div class="message-content-header">
      <h4>{{ $t("smsRegistrations.messagingContentHeader") }}</h4>
    </div>
    <div class="keywords-wrapper">
      <h5>{{ $t("smsRegistrations.messagingContentCommandKeyWords") }}</h5>
      <span v-html="$t('smsRegistrations.messagingContentCommandKeyWordsInfo')" />
      <div class="stop-box">
        <div class="stop-wrapper">
          <text-field
            class="stop-keyword"
            :label="$t('smsRegistrations.stopKeyword')"
            :required="false"
            :readOnly="true"
            value="STOP,END,QUIT,CANCEL,UNSUBSCRIBE,REVOKE,OPT OUT"
          />
          <text-field
            class="stop-message"
            @textChanged="messageContentDetails.stopMessage = $event"
            :label="$t('smsRegistrations.stopMessage')"
            :readOnly="!messageContentDetails.stopMessageEditable"
            :disabled="readOnly"
            :value="messageContentDetails.stopMessage"
            :validation-message="messagesMinLengthErrorMessage"
            :pattern="messageLengthRegex"
            data-cy="content-stop-message"
          />
          <div class="icons-wrapper">
            <vwc-icon-button
              @click="handleEditStop"
              :disabled="readOnly"
              class="edit-icon"
              icon="edit-line"
            />
            <vwc-icon-button
              icon="reset-line"
              :disabled="readOnly || !messageContentDetails.stopMessageEditable"
              class="reset-button"
              @click="handleResetStop"
            />
          </div>
        </div>
      </div>
      <div class="help-wrapper">
        <span>{{ $t("smsRegistrations.messagingContentHelpInfo") }}</span>
        <div class="support-inputs">
          <text-field
            @textFieldFocusout="editHelpMessage"
            :placeholder="
              $t('smsRegistrations.messagingContentHelpSupportNumber')
            "
            :required="false"
            :disabled="readOnly"
            data-cy="content-support-phone-number-input"
            @textChanged="messageContentDetails.supportPhone = $event"
            :value="messageContentDetails.supportPhone"
          />
          <text-field
            @textFieldFocusout="editHelpMessage"
            :placeholder="
              $t('smsRegistrations.messagingContentHelpSupportEmail')
            "
            :required="false"
            :disabled="readOnly"
            data-cy="content-support-email-input"
            @textChanged="messageContentDetails.supportEmail = $event"
            :value="messageContentDetails.supportEmail"
          />
          <text-field
            @textFieldFocusout="editHelpMessage"
            :placeholder="
              $t('smsRegistrations.messagingContentHelpSupportUrl')
            "
            :required="false"
            :disabled="readOnly"
            data-cy="content-support-url-input"
            @textChanged="messageContentDetails.supportUrl = $event"
            :value="messageContentDetails.supportUrl"
          />
        </div>
        <div class="help-info">
          <text-field
            class="help-keyword"
            :label="$t('smsRegistrations.helpKeyword')"
            :required="false"
            :readOnly="true"
            value="HELP"
          />
          <text-field
            class="help-message"
            @textChanged="messageContentDetails.helpMessage = $event"
            :label="$t('smsRegistrations.helpMessage')"
            :value="messageContentDetails.helpMessage"
            :readOnly="!messageContentDetails.helpMessageEditable"
            :disabled="readOnly"
            :validation-message="messagesMinLengthErrorMessage"
            :pattern="messageLengthRegex"
            data-cy="content-help-message"
          />
          <div class="icons-wrapper">
            <vwc-icon-button
              @click="handleEditHelp"
              :disabled="readOnly"
              class="edit-icon"
              icon="edit-line"
            />
            <vwc-icon-button
              icon="reset-line"
              :disabled="readOnly || !messageContentDetails.helpMessageEditable"
              class="reset-button"
              @click="handleResetHelp"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="sample-messages">
      <h5>{{ $t("smsRegistrations.sampleMessages") }}</h5>
      <sample-messages
        slot="step-body-4"
        class="campaign-step"
        v-model="messageContentDetails.campaignSamples"
        :read-only="readOnly"
      ></sample-messages>
    </div>
  </div>
</template>

<!-- eslint-disable vue/no-mutating-props -->
<script>
import SampleMessages from "../campaigns/SampleMessages";
import TextField from "../VwcTextField";
import { OPT_IN_MESSAGE_REGEX } from "../../helpers/constants";

export default {
  name: "MessageContentType",
  components: {
    TextField,
    SampleMessages,
  },
  data() {
    return {
      messageLengthRegex: OPT_IN_MESSAGE_REGEX,
    };
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
    messageContentDetails: {
      type: Object,
      default: () => ({}),
    },
    companyName: {
      type: String,
      default: "",
    },
  },
  beforeMount() {
    if (!this.messageContentDetails.stopMessageEditable) {
      this.messageContentDetails.stopMessage = this.stopMessage;
    }
    if (!this.messageContentDetails.helpMessageEditable) {
      this.editHelpMessage();
    }
  },
  computed: {
    stopMessage() {
      return `${this.companyName} - You have successfully unsubscribed and will no longer receive messages. To resubscribe at any time, reply JOIN.`;
    },
    helpMessage() {
      return `${this.companyName} - For help reach us at`;
    },
    messagesMinLengthErrorMessage() {
      return this.$t("smsRegistrations.minLengthOptMessages");
    },
    messageContentDetailsValid() {
      const campaignSamples = Object.values(this.messageContentDetails.campaignSamples);
      const campaignSampleValidation = campaignSamples.length >= 2 &&
          campaignSamples.every((sample) => sample.length > 19 && sample.length < 1024);
      const stopMessageValidation = new RegExp(this.messageLengthRegex).test(
        this.messageContentDetails.stopMessage
      );
      const helpMessageValidation = new RegExp(this.messageLengthRegex).test(
        this.messageContentDetails.helpMessage
      );
      const supportValid =
        this.messageContentDetails.supportPhone.length > 0 ||
        this.messageContentDetails.supportEmail.length > 0 ||
        this.messageContentDetails.supportUrl.length > 0;
      return (
        !campaignSampleValidation ||
        !stopMessageValidation ||
        !helpMessageValidation ||
        !supportValid
      );
    },
  },
  methods: {
    handleEditStop() {
      this.messageContentDetails.stopMessageEditable = true;
    },
    handleResetStop() {
      this.messageContentDetails.stopMessageEditable = false;
      this.messageContentDetails.stopMessage = this.stopMessage;
    },
    handleEditHelp() {
      this.messageContentDetails.helpMessageEditable = true;
    },
    handleResetHelp() {
      this.messageContentDetails.helpMessageEditable = false;
      this.editHelpMessage();
    },
    editHelpMessage() {
      if (!this.messageContentDetails.helpMessageEditable) {
        this.messageContentDetails.helpMessage =
          this.helpMessage +
          " " +
          this.messageContentDetails.supportPhone +
          " " +
          this.messageContentDetails.supportEmail +
          " " +
          this.messageContentDetails.supportUrl;
      }
    },
  },
  watch: {
    messageContentDetailsValid: {
      immediate: true,
      handler() {
        this.$emit("pageValidityChange", !this.messageContentDetailsValid);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.message-content {
  background: #ffffff;
  border-radius: 8px;
  padding: 24px;
}

.message-content-header {
  margin-bottom: 24px;
}

.stop-box {
  padding: 24px 24px 16px;
  margin-top: 16px;
  border: 1px solid #cccccc;
  border-radius: 8px;
}

.stop-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.inline-icon-text {
  display: flex;
  gap: 8px;
}

.inline-icon-text > vwc-icon {
  width: 20px;
  height: 20px;
}

.inline-icon-text > span {
  align-self: center;
}

.stop-keyword {
  flex: 1.5;
  padding-right: 12px;
}

.stop-message {
  flex: 8;
}

.icons-wrapper {
  margin-left: 22px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex: 0.5;
  align-items: center;
}

.help-wrapper {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  border: 1px solid #cccccc;
  border-radius: 8px;
  padding: 24px;

  span {
    margin-bottom: 12px;
  }
}

.support-inputs {
  flex-direction: row;
  display: flex;

  vwc-textfield {
    margin-left: 12px;
  }
  vwc-textfield:first-child {
    margin-left: 0px;
  }
}

.help-info {
  display: flex;
  flex-direction: row;
}

.help-keyword {
  flex: 1.5;
  padding-right: 12px;
}

.help-message {
  flex: 8;
}

.sample-messages {
  margin-top: 24px;
  background: #ffffff;
  border-radius: 8px;
}
.edit-icon {
  cursor: pointer;
}
.reset-button {
  cursor: pointer;
}
</style>
